import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { candidatePortalApi, CandidateAccountProfile } from '../../api/candidatePortal';
import { useCandidateAuth } from './CandidateAuthContext';
import { CareersNav } from './CareersNav';
import { useToast } from '../../components/Toast';

/** A small, reusable read-only/edit toggle wrapper - every section on this page follows the same pattern: view a summary, click Edit, see the form, Save or Cancel returns to the summary. */
function EditableSection({ title, description, summary, isEditing, onEdit, onCancel, children }: {
  title: string; description?: string; summary: React.ReactNode; isEditing: boolean; onEdit: () => void; onCancel: () => void; children: React.ReactNode;
}) {
  return (
    <section className="card">
      <div className="page-header">
        <h2>{title}</h2>
        {!isEditing && <button className="link-button" onClick={onEdit}>Edit</button>}
      </div>
      {description && <p className="muted">{description}</p>}
      {isEditing ? children : summary}
      {isEditing && <button className="link-button" onClick={onCancel}>Cancel</button>}
    </section>
  );
}

export function MyCandidateProfilePage() {
  const { account, loading } = useCandidateAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  useEffect(() => { if (!loading && !account) navigate('/careers/login'); }, [loading, account, navigate]);

  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(false);
  const [editingLanguages, setEditingLanguages] = useState(false);
  const [editingReferences, setEditingReferences] = useState(false);

  const accountQuery = useQuery({ queryKey: ['my-candidate-account'], queryFn: () => candidatePortalApi.myAccount(), enabled: !!account });
  const profileQuery = useQuery({ queryKey: ['my-candidate-profile'], queryFn: () => candidatePortalApi.myProfile(), enabled: !!account });

  const personalForm = useForm<CandidateAccountProfile>({ values: accountQuery.data?.data });
  const savePersonal = useMutation({
    mutationFn: (v: CandidateAccountProfile) => candidatePortalApi.updateMyAccount(v),
    onSuccess: () => { showSuccess('Personal information saved.'); queryClient.invalidateQueries({ queryKey: ['my-candidate-account'] }); setEditingPersonal(false); },
    onError: (err) => showError(err instanceof Error ? err.message : 'Failed to save.'),
  });

  const eduForm = useForm<{ institution: string; degree?: string; fieldOfStudy?: string; startDate?: string; endDate?: string; isCurrent?: boolean }>();
  const addEducation = useMutation({
    mutationFn: () => candidatePortalApi.addEducation(eduForm.getValues()),
    onSuccess: () => { showSuccess('Qualification added.'); queryClient.invalidateQueries({ queryKey: ['my-candidate-profile'] }); eduForm.reset(); },
    onError: (err) => showError(err instanceof Error ? err.message : 'Failed to add.'),
  });

  const jobForm = useForm<{ employerName: string; positionTitle: string; startDate?: string; endDate?: string; isCurrent?: boolean; responsibilities?: string; reasonForLeaving?: string; supervisorName?: string; supervisorContact?: string }>();
  const addEmployment = useMutation({
    mutationFn: () => candidatePortalApi.addEmployment(jobForm.getValues()),
    onSuccess: () => { showSuccess('Employment added.'); queryClient.invalidateQueries({ queryKey: ['my-candidate-profile'] }); jobForm.reset(); },
    onError: (err) => showError(err instanceof Error ? err.message : 'Failed to add.'),
  });

  const langForm = useForm<{ language: string; speakLevel?: string; readLevel?: string; writeLevel?: string }>();
  const addLanguage = useMutation({
    mutationFn: () => candidatePortalApi.addLanguage(langForm.getValues()),
    onSuccess: () => { showSuccess('Language added.'); queryClient.invalidateQueries({ queryKey: ['my-candidate-profile'] }); langForm.reset(); },
    onError: (err) => showError(err instanceof Error ? err.message : 'Failed to add.'),
  });

  const refForm = useForm<{ fullName: string; relationship?: string; organization?: string; phone?: string; email?: string }>();
  const addReference = useMutation({
    mutationFn: () => candidatePortalApi.addReference(refForm.getValues()),
    onSuccess: () => { showSuccess('Reference added.'); queryClient.invalidateQueries({ queryKey: ['my-candidate-profile'] }); refForm.reset(); },
    onError: (err) => showError(err instanceof Error ? err.message : 'Failed to add.'),
  });

  if (loading || !account) return <div className="careers-page"><CareersNav /><p className="muted">Loading…</p></div>;
  const profile = profileQuery.data?.data;
  const p = accountQuery.data?.data;

  return (
    <div className="careers-page">
      <CareersNav />
      <h1>My Profile</h1>
      <p className="muted">Build your profile once - it's reused whenever you apply with your account, and reduces repeated data entry across applications.</p>

      {p && (
        <EditableSection
          title="Personal Information"
          isEditing={editingPersonal}
          onEdit={() => setEditingPersonal(true)}
          onCancel={() => setEditingPersonal(false)}
          summary={
            <dl className="profile-summary">
              <div><dt>Full Name</dt><dd>{p.fullName}</dd></div>
              <div><dt>Email</dt><dd>{p.email}</dd></div>
              <div><dt>Phone</dt><dd>{p.phone || '-'}</dd></div>
              <div><dt>Date of Birth</dt><dd>{p.dateOfBirth || '-'}</dd></div>
              <div><dt>Gender</dt><dd>{p.gender || '-'}</dd></div>
              <div><dt>Nationality</dt><dd>{p.nationality || '-'}</dd></div>
              <div><dt>Address</dt><dd>{p.address || '-'}</dd></div>
              <div><dt>Current Employer</dt><dd>{p.currentEmployer || '-'}</dd></div>
              <div><dt>Marital Status</dt><dd>{p.maritalStatus || '-'}</dd></div>
              <div><dt>Dependents</dt><dd>{p.numberOfDependents ?? '-'}</dd></div>
              <div><dt>LinkedIn / Portfolio</dt><dd>{p.linkedinUrl || '-'}</dd></div>
              <div><dt>Willing to Travel</dt><dd>{p.willingToTravel === null ? '-' : p.willingToTravel ? 'Yes' : 'No'}</dd></div>
              <div><dt>Preferred Duty Station</dt><dd>{p.preferredDutyStation || '-'}</dd></div>
              <div><dt>Accommodation Needs</dt><dd>{p.accommodationNeeds || '-'}</dd></div>
            </dl>
          }
        >
          <form onSubmit={personalForm.handleSubmit((v) => savePersonal.mutate(v))}>
            <label>Full Name<input {...personalForm.register('fullName', { required: true })} /></label>
            <label>Email<input value={p.email} disabled /></label>
            <label>Phone<input {...personalForm.register('phone')} /></label>
            <label>Date of Birth<input type="date" {...personalForm.register('dateOfBirth')} /></label>
            <label>
              Gender
              <select {...personalForm.register('gender')}>
                <option value="">- prefer not to say -</option>
                <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </label>
            <label>Nationality<input {...personalForm.register('nationality')} /></label>
            <label>Address<textarea rows={2} {...personalForm.register('address')} /></label>
            <label>Current Employer<input {...personalForm.register('currentEmployer')} /></label>
            <label>
              Marital Status
              <select {...personalForm.register('maritalStatus')}>
                <option value="">- prefer not to say -</option>
                <option value="single">Single</option><option value="married">Married</option><option value="divorced">Divorced</option><option value="widowed">Widowed</option>
              </select>
            </label>
            <label>Number of Dependents<input type="number" min="0" {...personalForm.register('numberOfDependents', { valueAsNumber: true })} /></label>
            <label>LinkedIn / Portfolio URL<input {...personalForm.register('linkedinUrl')} /></label>
            <label><input type="checkbox" {...personalForm.register('willingToTravel')} /> Willing to travel</label>
            <label>Preferred Duty Station<input {...personalForm.register('preferredDutyStation')} /></label>
            <label>Accommodation Needs (optional - any reasonable accommodation you'd like us to know about)<textarea rows={2} {...personalForm.register('accommodationNeeds')} /></label>
            <div className="form-actions"><button type="submit" disabled={savePersonal.isPending}>{savePersonal.isPending ? 'Saving…' : 'Save'}</button></div>
          </form>
        </EditableSection>
      )}

      <EditableSection
        title="Education"
        isEditing={editingEducation}
        onEdit={() => setEditingEducation(true)}
        onCancel={() => setEditingEducation(false)}
        summary={
          <>
            {profile?.education.length === 0 && <p className="muted">No qualifications added yet.</p>}
            {profile?.education.map((e, i) => (
              <div key={i} className="p11-repeat-block">
                <strong>{e.institution}</strong> - {e.degree} ({e.fieldOfStudy})
                {e.startDate && <p className="muted">{e.startDate} - {e.isCurrent ? 'Present' : e.endDate}</p>}
              </div>
            ))}
          </>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); addEducation.mutate(); }}>
          <label>Institution<input {...eduForm.register('institution', { required: true })} /></label>
          <label>Degree<input {...eduForm.register('degree')} /></label>
          <label>Field of Study<input {...eduForm.register('fieldOfStudy')} /></label>
          <label>Start Date<input type="date" {...eduForm.register('startDate')} /></label>
          <label>End Date<input type="date" {...eduForm.register('endDate')} /></label>
          <label><input type="checkbox" {...eduForm.register('isCurrent')} /> Currently studying here</label>
          <div className="form-actions"><button type="submit" disabled={addEducation.isPending}>Add Qualification</button></div>
        </form>
      </EditableSection>

      <EditableSection
        title="Employment History"
        isEditing={editingEmployment}
        onEdit={() => setEditingEmployment(true)}
        onCancel={() => setEditingEmployment(false)}
        summary={
          <>
            {profile?.employmentHistory.length === 0 && <p className="muted">No employment history added yet.</p>}
            {profile?.employmentHistory.map((e, i) => (
              <div key={i} className="p11-repeat-block">
                <strong>{e.positionTitle}</strong> at {e.employerName}
                {e.startDate && <p className="muted">{e.startDate} - {e.isCurrent ? 'Present' : e.endDate}</p>}
                {e.responsibilities && <p className="muted">{e.responsibilities}</p>}
              </div>
            ))}
          </>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); addEmployment.mutate(); }}>
          <label>Employer<input {...jobForm.register('employerName', { required: true })} /></label>
          <label>Position Title<input {...jobForm.register('positionTitle', { required: true })} /></label>
          <label>Start Date<input type="date" {...jobForm.register('startDate')} /></label>
          <label>End Date<input type="date" {...jobForm.register('endDate')} /></label>
          <label><input type="checkbox" {...jobForm.register('isCurrent')} /> Currently working here</label>
          <label>Responsibilities<textarea rows={2} {...jobForm.register('responsibilities')} /></label>
          <label>Reason for Leaving<input {...jobForm.register('reasonForLeaving')} /></label>
          <label>Supervisor Name<input {...jobForm.register('supervisorName')} /></label>
          <label>Supervisor Contact<input {...jobForm.register('supervisorContact')} /></label>
          <div className="form-actions"><button type="submit" disabled={addEmployment.isPending}>Add Employment</button></div>
        </form>
      </EditableSection>

      <EditableSection
        title="Languages"
        isEditing={editingLanguages}
        onEdit={() => setEditingLanguages(true)}
        onCancel={() => setEditingLanguages(false)}
        summary={
          <>
            {profile?.languages.length === 0 && <p className="muted">No languages added yet.</p>}
            {profile?.languages.map((l, i) => <p key={i} className="p11-repeat-block">{l.language} - Speak: {l.speakLevel ?? '-'} · Read: {l.readLevel ?? '-'} · Write: {l.writeLevel ?? '-'}</p>)}
          </>
        }
      >
        <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addLanguage.mutate(); }}>
          <label>Language<input {...langForm.register('language', { required: true })} /></label>
          <label>
            Speak
            <select {...langForm.register('speakLevel')}>
              <option value="">- select -</option>
              <option value="basic">Basic</option><option value="intermediate">Intermediate</option><option value="fluent">Fluent</option><option value="native">Native</option>
            </select>
          </label>
          <label>
            Read
            <select {...langForm.register('readLevel')}>
              <option value="">- select -</option>
              <option value="basic">Basic</option><option value="intermediate">Intermediate</option><option value="fluent">Fluent</option><option value="native">Native</option>
            </select>
          </label>
          <label>
            Write
            <select {...langForm.register('writeLevel')}>
              <option value="">- select -</option>
              <option value="basic">Basic</option><option value="intermediate">Intermediate</option><option value="fluent">Fluent</option><option value="native">Native</option>
            </select>
          </label>
          <button type="submit" disabled={addLanguage.isPending}>Add</button>
        </form>
      </EditableSection>

      <EditableSection
        title="References"
        isEditing={editingReferences}
        onEdit={() => setEditingReferences(true)}
        onCancel={() => setEditingReferences(false)}
        summary={
          <>
            {profile?.references.length === 0 && <p className="muted">No references added yet.</p>}
            {profile?.references.map((r, i) => <p key={i} className="p11-repeat-block">{r.fullName} - {r.relationship} at {r.organization} ({r.email})</p>)}
          </>
        }
      >
        <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addReference.mutate(); }}>
          <label>Full Name<input {...refForm.register('fullName', { required: true })} /></label>
          <label>Relationship<input {...refForm.register('relationship')} /></label>
          <label>Organization<input {...refForm.register('organization')} /></label>
          <label>Phone<input {...refForm.register('phone')} /></label>
          <label>Email<input type="email" {...refForm.register('email')} /></label>
          <button type="submit" disabled={addReference.isPending}>Add</button>
        </form>
      </EditableSection>
    </div>
  );
}
